"use client";

import { AlertCircle, Home, RefreshCcw } from "lucide-react";
import Link from "next/link";
import posthog from "posthog-js";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";

export default function UserError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("User page error caught:", error);

    posthog.captureException(error, {
      digest: error.digest,
      boundary: "user-error",
    });

    // Automatic reload for ChunkLoadError
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("CSS chunk");

    if (isChunkError) {
      const lastReload = localStorage.getItem("last_chunk_reload");
      const now = Date.now();
      if (!lastReload || now - Number.parseInt(lastReload, 10) > 10000) {
        localStorage.setItem("last_chunk_reload", now.toString());
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="relative min-h-[60vh] flex flex-col items-center justify-center p-6 text-center overflow-hidden">
      {/* Background Decorations */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0193cd 1px, transparent 1px), linear-gradient(90deg, #0193cd 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="absolute top-[20%] left-[20%] w-[30%] h-[30%] bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-md w-full p-8 rounded-3xl border border-border bg-card/60 backdrop-blur-md shadow-xl space-y-6">
        <div className="mx-auto bg-primary/10 p-4 rounded-2xl w-16 h-16 flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-primary" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-black tracking-tight">Something went wrong!</h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            The page encountered an unexpected problem. Please try reloading. If the issue
            continues, feel free to head back home.
          </p>
        </div>

        {process.env.NODE_ENV === "development" && (
          <div className="w-full p-4 bg-muted/50 rounded-xl text-left overflow-auto max-h-40 font-mono text-xs text-red-500 border border-red-500/10">
            <p className="font-bold mb-1">
              {error.name}: {error.message}
            </p>
            {error.stack && <pre className="opacity-70">{error.stack}</pre>}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          <Button
            onClick={() => window.location.reload()}
            className="rounded-full gap-2 px-6 shadow-md shadow-primary/10 cursor-pointer"
          >
            <RefreshCcw className="w-4 h-4" />
            Reload Page
          </Button>

          <Button variant="secondary" onClick={reset} className="rounded-full px-6 cursor-pointer">
            Try recovering
          </Button>

          <Link href="/">
            <Button variant="outline" className="rounded-full gap-2 px-6 cursor-pointer">
              <Home className="w-4 h-4" />
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
